import { useCallback, useMemo } from 'react'

import { isEmpty } from 'lodash-es'

import { useFilterBenefitsForExplorer } from './useFilterBenefitsForExplorer'
import { Benefit, BenefitGroup } from '../../../models/benefit/Benefit'
import { CustomerCoreBenefit } from '../../../models/customer/Customer'
import {
  COMMON_MIXPANEL_PROPERTIES,
  INTEGRATED_BENEFITS_CATEGORY,
  INTEGRATED_BENEFITS_EVENTS,
  INTEGRATED_BENEFITS_MIXPANEL_PROPERTIES,
  INTEGRATED_BENEFITS_SECTION_PROPERTIES,
  INTEGRATED_BENEFITS_SECTION_TYPE,
  PATHWAYS_EVENTS,
  TOOLKIT_BENEFIT_NAME,
} from '../../common/constants/mixpanelConstants'

type Params = {
  registeredCountry?: string
  matchFor: string
  userLyraId: string
  customerLyraId?: number
  recommendedBenefitGroupsForUser: BenefitGroup[]
  allBenefitGroupsForUser: BenefitGroup[]
  maxBenefitsToShowInExplorer: number
  lyraNicotineQuitlineEnabled: boolean
  shouldShowNeurodiversityToolkit?: boolean
  shouldShowCriticalIncidentSupportToolkit?: boolean
  shouldShowManagerHub?: boolean
  coreBenefits?: CustomerCoreBenefit[]
}

export type CallTimeParams = {
  event: INTEGRATED_BENEFITS_EVENTS | PATHWAYS_EVENTS
  benefitClicked?: string
  entryPoint?: string
  categoryOfClickedBenefit?: string
  benefitSectionType?: INTEGRATED_BENEFITS_SECTION_TYPE
  additionalMixpanelEvent?: Record<string, unknown>
}

export type OnPressBenefitParams = {
  benefitClicked?: string
  categoryOfClickedBenefit?: string
  benefitUrl: string
  benefitSectionType?: INTEGRATED_BENEFITS_SECTION_TYPE
  benefit?: Benefit
}

export type OnPressBenefit = (params: OnPressBenefitParams) => void

type MixpanelBenefit = {
  vendor: string
  category: string
  isRecommended?: boolean
}

const LYRA_OFFERING_BENEFIT_BASE = {
  category: INTEGRATED_BENEFITS_CATEGORY.LYRA_OFFERING,
  isRecommended: false,
}

const makeMixpanelBenefitsArray = (
  benefitsCategoryMap: Map<string, Benefit[]>,
  options: { isRecommended?: boolean } = {},
): MixpanelBenefit[] =>
  Object.entries(Object.fromEntries(benefitsCategoryMap)).flatMap(([category, benefits]) => {
    const benefitsForMixpanel = benefits.map((b) => ({ vendor: b.displayName, category, ...options }))
    return benefitsForMixpanel
  })

export const useGetBenefitsEventProps = ({
  registeredCountry,
  matchFor,
  userLyraId,
  customerLyraId,
  recommendedBenefitGroupsForUser = [],
  allBenefitGroupsForUser = [],
  lyraNicotineQuitlineEnabled,
  maxBenefitsToShowInExplorer = 3,
  shouldShowNeurodiversityToolkit = false,
  shouldShowCriticalIncidentSupportToolkit = false,
  shouldShowManagerHub = false,
  coreBenefits = [],
}: Params) => {
  const otherBenefitsForUser = useMemo(() => {
    const recommendedBenefitGroupKeys = new Set((recommendedBenefitGroupsForUser ?? []).map((bg) => bg.benefitCategory))
    return (allBenefitGroupsForUser ?? []).filter((bg) => !recommendedBenefitGroupKeys.has(bg.benefitCategory))
  }, [recommendedBenefitGroupsForUser, allBenefitGroupsForUser])

  const displayedRecommendedBenefitsInExplorer = makeMixpanelBenefitsArray(
    useFilterBenefitsForExplorer({
      benefitGroups: recommendedBenefitGroupsForUser,
      maxBenefitsToShowInExplorer,
    }),
    {
      isRecommended: true,
    },
  )

  const displayedOtherBenefitsInExplorer = makeMixpanelBenefitsArray(
    useFilterBenefitsForExplorer({
      benefitGroups: otherBenefitsForUser,
      maxBenefitsToShowInExplorer,
    }),
    {
      isRecommended: false,
    },
  )

  const recommendedBenefitsGroupsMap = new Map<string, Benefit[]>(
    recommendedBenefitGroupsForUser.map(({ benefitCategory, benefits }) => [benefitCategory, benefits]),
  )
  const allRecommendedBenefits = makeMixpanelBenefitsArray(recommendedBenefitsGroupsMap, {
    isRecommended: true,
  })

  const otherBenefitsGroupsMap = new Map<string, Benefit[]>(
    otherBenefitsForUser.map(({ benefitCategory, benefits }) => [benefitCategory, benefits]),
  )
  const allOtherBenefits = makeMixpanelBenefitsArray(otherBenefitsGroupsMap, {
    isRecommended: false,
  })

  const customerCoreBenefits: MixpanelBenefit[] = coreBenefits.map(({ analyticsName }) => ({
    vendor: analyticsName,
    ...LYRA_OFFERING_BENEFIT_BASE,
  }))

  return useCallback(
    ({ event, entryPoint, benefitClicked, categoryOfClickedBenefit, benefitSectionType }: CallTimeParams) => {
      const commonProps = {
        [COMMON_MIXPANEL_PROPERTIES.LYRA_ID]: userLyraId,
        [COMMON_MIXPANEL_PROPERTIES.CUSTOMER_ID]: customerLyraId,
        [COMMON_MIXPANEL_PROPERTIES.REGISTERED_COUNTRY]: registeredCountry,
        [INTEGRATED_BENEFITS_MIXPANEL_PROPERTIES.MATCH_FOR]: matchFor,
        [INTEGRATED_BENEFITS_MIXPANEL_PROPERTIES.BENEFITS]: [
          ...displayedRecommendedBenefitsInExplorer,
          ...displayedOtherBenefitsInExplorer,
        ],
        [COMMON_MIXPANEL_PROPERTIES.ENTRY_POINT]: entryPoint,
      }

      switch (event) {
        case INTEGRATED_BENEFITS_EVENTS.VIEW_INTEGRATED_BENEFITS_PAGE:
          const toolkitBenefits: MixpanelBenefit[] = []
          if (shouldShowCriticalIncidentSupportToolkit) {
            toolkitBenefits.push({
              vendor: TOOLKIT_BENEFIT_NAME.TRAUMA_TOOLKIT,
              ...LYRA_OFFERING_BENEFIT_BASE,
            })
          }
          if (shouldShowNeurodiversityToolkit) {
            toolkitBenefits.push({
              vendor: TOOLKIT_BENEFIT_NAME.NEURODIVERSITY_TOOLKIT,
              ...LYRA_OFFERING_BENEFIT_BASE,
            })
          }
          if (shouldShowManagerHub) {
            toolkitBenefits.push({
              vendor: TOOLKIT_BENEFIT_NAME.MANAGER_HUB_TOOLKIT,
              ...LYRA_OFFERING_BENEFIT_BASE,
            })
          }

          return {
            ...commonProps,
            [INTEGRATED_BENEFITS_MIXPANEL_PROPERTIES.BENEFITS]: [
              ...allRecommendedBenefits,
              ...allOtherBenefits,
              ...customerCoreBenefits,
              ...toolkitBenefits,
            ],
            lyraNicotineQuitlineEnabled,
          }
        case INTEGRATED_BENEFITS_EVENTS.VIEW_INTEGRATED_BENEFIT_MODAL:
        case INTEGRATED_BENEFITS_EVENTS.CLICK_INTEGRATED_BENEFIT_SSO:
        case INTEGRATED_BENEFITS_EVENTS.CLOSE_INTEGRATED_BENEFIT_MODAL:
        case INTEGRATED_BENEFITS_EVENTS.CLICK_INTEGRATED_BENEFIT:
          const clickedBenefit = [...allRecommendedBenefits, ...allOtherBenefits].filter(
            (benefit) => benefit.vendor === benefitClicked && benefit.category === categoryOfClickedBenefit,
          )

          // Create fallback benefit data, only including category if it's defined
          const fallbackBenefit: any = { vendor: benefitClicked, isRecommended: false }
          if (categoryOfClickedBenefit) {
            fallbackBenefit.category = categoryOfClickedBenefit
          }

          const benefitData = !isEmpty(clickedBenefit) ? clickedBenefit : [fallbackBenefit]
          return {
            ...commonProps,
            // The clicked tile belongs in benefits[].vendor; benefit_partner is a customer-level super property.
            [INTEGRATED_BENEFITS_MIXPANEL_PROPERTIES.BENEFITS]: benefitData,
            [INTEGRATED_BENEFITS_SECTION_PROPERTIES.BENEFITS_DIRECTORY_SECTION]:
              benefitSectionType ?? INTEGRATED_BENEFITS_SECTION_TYPE.CORE_BENEFITS,
          }
        case INTEGRATED_BENEFITS_EVENTS.VIEW_INTEGRATED_BENEFITS_ENTRYCARD:
          return {
            ...commonProps,
            [INTEGRATED_BENEFITS_MIXPANEL_PROPERTIES.BENEFITS]: displayedRecommendedBenefitsInExplorer,
          }
        default:
          return commonProps
      }
    },
    [
      userLyraId,
      customerLyraId,
      registeredCountry,
      matchFor,
      displayedRecommendedBenefitsInExplorer,
      displayedOtherBenefitsInExplorer,
      allRecommendedBenefits,
      allOtherBenefits,
      customerCoreBenefits,
      lyraNicotineQuitlineEnabled,
      shouldShowNeurodiversityToolkit,
      shouldShowCriticalIncidentSupportToolkit,
      shouldShowManagerHub,
    ],
  )
}
